<hlm-dialog-surface size="lg" class="min-h-[460px]">
  <hlm-dialog-header>
    <div hlmDialogHeading>
      <h2 hlmDialogTitle class="font-display text-xl">
        {{ translate('billingItems.dialog.title', 'Add Billing Item') }}
      </h2>
    </div>
    @if (customerMeta()) {
      <!--
        Header directive is `relative` — pin the customer meta to the exact
        horizontal centre of the header so it reads as a stable caption above
        the two-column form regardless of title length or the close-button.
      -->
      <span
        class="font-mono-feat pointer-events-none absolute left-1/2 top-5 max-w-[24rem] -translate-x-1/2 truncate text-xs text-muted-foreground"
        [title]="customerMeta()"
      >
        {{ customerMeta() }}
      </span>
    }
    <span class="ml-auto"></span>
    <button hlmDialogClose tabindex="-1" (click)="cancel()">
      <hlm-icon name="lucideX" size="1rem" />
    </button>
  </hlm-dialog-header>

  <section hlmDialogBody class="relative">
    @if (state() === 'LOADING') {
      <div
        class="absolute inset-0 z-10 grid place-items-center bg-card/60 backdrop-blur-[1px]"
        role="status"
        [attr.aria-label]="translate('billingItems.loading', 'Loading')"
      >
        <hlm-spinner size="20" />
      </div>
    }

    <form
      [formGroup]="form"
      (ngSubmit)="submit()"
      class="flex flex-col gap-4"
      [class.opacity-60]="state() === 'LOADING'"
    >
      @if (noGroups()) {
        <div
          class="rounded-md border border-border bg-muted/50 p-3 text-sm text-muted-foreground"
          role="alert"
        >
          {{ translate('billingItems.empty.noGroups', 'No billing groups available for this customer.') }}
        </div>
      }

      @if (noUserAccounts()) {
        <div
          class="rounded-md border border-border bg-muted/50 p-3 text-sm text-muted-foreground"
          role="alert"
        >
          {{ translate('billingItems.empty.noUserAccounts', 'No user accounts on this customer.') }}
        </div>
      }

      @if (noCategoriesForUa()) {
        <div
          class="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-foreground"
          role="alert"
        >
          <hlm-icon name="lucideInfo" size="0.95rem" class="mt-0.5 flex-none text-destructive" />
          <span>
            {{ translate(
              'billingItems.empty.noCategoriesForUa',
              'The selected user account has no billable categories. Choose a different user account.'
            ) }}
          </span>
        </div>
      }

      <div class="grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-2">
        <!-- Left column — WHAT'S BEING BILLED -->
        <div class="flex flex-col gap-4">
          <p
            class="font-mono-feat text-xs font-semibold uppercase tracking-[0.10em] text-muted-foreground"
          >
            {{ translate('billingItems.section.whatBilled', "What's being billed") }}
          </p>

          <div class="relative">
            <hlm-form-field>
              <label hlmLabel for="addBillingGroup">
                {{ translate('billingItems.field.category', 'Category') }}
                <span class="text-destructive">*</span>
              </label>
              <input
                id="addBillingGroup"
                hlmInput
                class="pr-10 text-ellipsis"
                formControlName="groupSearch"
                [matAutocomplete]="groupAuto"
                [placeholder]="translate('billingItems.field.categoryPlaceholder', 'Select a category…')"
                (input)="onGroupSearch()"
                (focus)="selectInputContents($event)"
                [attr.aria-required]="true"
                [attr.aria-invalid]="form.controls.groupId.touched && form.controls.groupId.invalid"
              />
              @if (form.controls.groupId.touched && form.controls.groupId.invalid) {
                <p hlmFieldError>
                  {{ translate('billingItems.field.categoryRequired', 'Please choose a category.') }}
                </p>
              }
            </hlm-form-field>
            <mat-autocomplete
              #groupAuto="matAutocomplete"
              [displayWith]="displayGroup"
              (optionSelected)="onGroupSelected($event)"
              (opened)="groupPanelOpen.set(true)"
              (closed)="groupPanelOpen.set(false)"
            >
              @for (g of filteredGroupOptions(); track g.id) {
                <mat-option [value]="g">
                  <span class="truncate">{{ g.label }}</span>
                </mat-option>
              }
              @if (filteredGroupOptions().length === 0 && groupFilter()) {
                <mat-option disabled>
                  {{ translate('billingItems.field.categoryEmpty', 'No categories match.') }}
                </mat-option>
              }
            </mat-autocomplete>
            @if (showGroupTrash()) {
              <button
                hlmBtn
                variant="ghost"
                size="icon"
                type="button"
                tabindex="-1"
                class="absolute right-1 top-7 h-7 w-7"
                (mousedown)="clearGroup($event)"
                (click)="clearGroup($event)"
                aria-label="Clear selection"
              >
                <hlm-icon name="lucideTrash2" size="0.8rem" />
              </button>
            } @else {
              <span
                aria-hidden="true"
                class="pointer-events-none absolute right-2 top-7 grid h-7 w-7 place-items-center text-muted-foreground"
              >
                <hlm-icon name="lucideChevronDown" size="0.9rem" />
              </span>
            }
          </div>

          <div class="relative">
            <hlm-form-field>
              <!-- Help icon is a sibling of the label, not inside it: a click must not
                   focus the input, and its name must not join the input's name (EB-4502). -->
              <div class="flex items-center gap-1">
                <label hlmLabel for="addBillingFeature">
                  {{ translate('billingItems.field.feature', 'Feature') }}
                  <span class="text-destructive">*</span>
                </label>
                @if (selectedFeatureOption(); as selected) {
                  <cmt-feature-help [texts]="selected.texts" />
                }
              </div>
              <input
                id="addBillingFeature"
                hlmInput
                class="pr-10 text-ellipsis"
                formControlName="featureSearch"
                [matAutocomplete]="featureAuto"
                [placeholder]="translate('billingItems.field.featurePlaceholder', 'Select a feature…')"
                [attr.disabled]="form.controls.featureName.disabled ? true : null"
                (input)="onFeatureSearch()"
                (focus)="selectInputContents($event)"
                [attr.aria-invalid]="form.controls.featureName.touched && form.controls.featureName.invalid"
              />
              @if (selectedGroupHasNoFeatures()) {
                <p hlmFieldHint>
                  {{ translate('billingItems.field.featureNone', 'No addable features in this group.') }}
                </p>
              } @else if (form.controls.featureName.touched && form.controls.featureName.invalid) {
                <p hlmFieldError>
                  {{ translate('billingItems.field.featureRequired', 'Please choose a feature.') }}
                </p>
              }
            </hlm-form-field>
            <mat-autocomplete
              #featureAuto="matAutocomplete"
              [displayWith]="displayFeature"
              (optionSelected)="onFeatureSelected($event)"
              (opened)="featurePanelOpen.set(true)"
              (closed)="featurePanelOpen.set(false)"
            >
              @for (f of filteredFeatures(); track f.feature) {
                <mat-option [value]="f" [attr.data-testid]="'add-billing-feature-' + f.feature">
                  <!-- Two lines (EB-4502): English, then the KV wording staff know. -->
                  <span class="block py-1 leading-snug">
                    <span class="block">{{ f.texts.english }}</span>
                    @if (f.secondLine) {
                      <span class="block text-sm text-muted-foreground">{{ f.secondLine }}</span>
                    }
                  </span>
                </mat-option>
              }
              @if (filteredFeatures().length === 0 && featureFilter()) {
                <mat-option disabled>
                  {{ translate('billingItems.field.featureNoMatch', 'No matches') }}
                </mat-option>
              }
            </mat-autocomplete>
            @if (showFeatureTrash()) {
              <button
                hlmBtn
                variant="ghost"
                size="icon"
                type="button"
                tabindex="-1"
                class="absolute right-1 top-7 h-7 w-7"
                (mousedown)="clearFeature($event)"
                (click)="clearFeature($event)"
                aria-label="Clear selection"
              >
                <hlm-icon name="lucideTrash2" size="0.8rem" />
              </button>
            } @else {
              <span
                aria-hidden="true"
                class="pointer-events-none absolute right-2 top-7 grid h-7 w-7 place-items-center text-muted-foreground"
              >
                <hlm-icon name="lucideChevronDown" size="0.9rem" />
              </span>
            }
          </div>

          <hlm-form-field>
            <label hlmLabel for="addBillingDescription">
              {{ translate('billingItems.field.description', 'Description') }}
            </label>
            <textarea
              id="addBillingDescription"
              hlmTextarea
              formControlName="description"
              rows="3"
              maxlength="500"
              [placeholder]="translate('billingItems.field.descriptionPlaceholder', 'Optional note for the billing row')"
            ></textarea>
            @if (form.controls.description.errors?.['maxlength']) {
              <p hlmFieldError>
                {{ translate('billingItems.field.descriptionTooLong', 'Description is too long (max 500 characters).') }}
              </p>
            }
          </hlm-form-field>
        </div>

        <!-- Right column — WHO & HOW MUCH -->
        <div class="flex flex-col gap-4">
          <p
            class="font-mono-feat text-xs font-semibold uppercase tracking-[0.10em] text-muted-foreground"
          >
            {{ translate('billingItems.section.whoHowMuch', 'Who & how much') }}
          </p>

          <div class="relative">
            <hlm-form-field>
              <label hlmLabel for="addBillingUa">
                {{ translate('billingItems.field.userAccount', 'User Account') }}
                <span class="text-destructive">*</span>
              </label>
              <input
                id="addBillingUa"
                hlmInput
                formControlName="userAccountSearch"
                [matAutocomplete]="uaAuto"
                [placeholder]="translate('billingItems.field.userAccountPlaceholder', 'Select a user account…')"
                (input)="onUaSearch()"
                (focus)="selectInputContents($event)"
                [attr.aria-required]="true"
                [attr.aria-invalid]="form.controls.userAccountId.touched && form.controls.userAccountId.invalid"
              />
              @if (form.controls.userAccountId.touched && form.controls.userAccountId.invalid) {
                <p hlmFieldError>
                  {{ translate('billingItems.field.userAccountRequired', 'Please choose a user account.') }}
                </p>
              }
              @if (showFeatureMismatchBadge()) {
                <p hlmFieldHint class="flex items-start gap-1.5 text-muted-foreground">
                  <hlm-icon name="lucideInfo" size="0.9rem" class="mt-0.5" />
                  <span>
                    {{
                      translate(
                        'billingItems.badge.featureMismatch',
                        'This feature is not assigned to the selected User Account. The server will re-check on submit.'
                      )
                    }}
                  </span>
                </p>
              }
            </hlm-form-field>
            <mat-autocomplete
              #uaAuto="matAutocomplete"
              [displayWith]="displayUserAccount"
              (optionSelected)="onUaSelected($event)"
              (opened)="uaPanelOpen.set(true)"
              (closed)="uaPanelOpen.set(false)"
            >
              @for (ua of filteredUserAccounts(); track ua.id) {
                <mat-option [value]="ua">
                  <span class="flex w-full items-center justify-between gap-2">
                    <span class="truncate">
                      {{ ua.fullName || ua.firstName + ' ' + ua.lastName }}
                    </span>
                    <span class="font-mono-feat text-xs text-muted-foreground">{{ ua.id }}</span>
                  </span>
                </mat-option>
              }
              @if (filteredUserAccounts().length === 0 && uaFilter()) {
                <mat-option disabled>
                  {{ translate('billingItems.field.userAccountNoMatch', 'No matches') }}
                </mat-option>
              }
            </mat-autocomplete>
            @if (showUaTrash()) {
              <button
                hlmBtn
                variant="ghost"
                size="icon"
                type="button"
                tabindex="-1"
                class="absolute right-1 top-7 h-7 w-7"
                (mousedown)="clearUa($event)"
                (click)="clearUa($event)"
                aria-label="Clear selection"
              >
                <hlm-icon name="lucideTrash2" size="0.8rem" />
              </button>
            } @else {
              <span
                aria-hidden="true"
                class="pointer-events-none absolute right-2 top-7 grid h-7 w-7 place-items-center text-muted-foreground"
              >
                <hlm-icon name="lucideChevronDown" size="0.9rem" />
              </span>
            }
          </div>

          <div class="grid grid-cols-1 gap-3 md:grid-cols-2 md:gap-4">
            @if (isUsageCreditGroup()) {
              <hlm-form-field>
                <label hlmLabel for="addBillingValidUntil">
                  {{ translate('billingItems.field.validUntil', 'Valid until') }}
                  <span class="text-destructive">*</span>
                </label>
                <input
                  id="addBillingValidUntil"
                  hlmInput
                  type="date"
                  formControlName="validUntil"
                />
                @if (form.controls.validUntil.touched && form.controls.validUntil.errors?.['required']) {
                  <p hlmFieldError>
                    {{ translate('billingItems.field.validUntilRequired', 'Please choose a valid-until date.') }}
                  </p>
                }
              </hlm-form-field>
            } @else {
              <hlm-form-field>
                <label hlmLabel for="addBillingQuantity">
                  {{ translate('billingItems.field.quantity', 'Quantity') }}
                  <span class="text-destructive">*</span>
                </label>
                <input
                  id="addBillingQuantity"
                  hlmInput
                  formControlName="quantityInput"
                  inputmode="decimal"
                  autocomplete="off"
                  [attr.aria-invalid]="form.controls.quantityInput.touched && form.controls.quantityInput.invalid"
                />
                @if (form.controls.quantityInput.touched && form.controls.quantityInput.errors?.['required']) {
                  <p hlmFieldError>
                    {{ translate('billingItems.field.quantityRequired', 'Please enter a quantity.') }}
                  </p>
                } @else if (form.controls.quantityInput.touched && form.controls.quantityInput.errors?.['localeDecimal']) {
                  <p hlmFieldError>
                    {{ translate('billingItems.field.quantityInvalid', 'Enter a valid number.') }}
                  </p>
                } @else if (form.controls.quantityInput.touched && form.controls.quantityInput.errors?.['quantityNotPositive']) {
                  <p hlmFieldError>
                    {{ translate('billingItems.field.quantityNotPositive', 'Quantity must be greater than 0.') }}
                  </p>
                }
              </hlm-form-field>
            }

            <hlm-form-field>
              <label hlmLabel for="addBillingUnitPrice">
                {{ translate('billingItems.field.unitPrice', 'Unit Price') }}
                <span class="text-destructive">*</span>
              </label>
              <input
                id="addBillingUnitPrice"
                hlmInput
                formControlName="unitPriceInput"
                inputmode="decimal"
                autocomplete="off"
                [attr.aria-invalid]="form.controls.unitPriceInput.touched && form.controls.unitPriceInput.invalid"
              />
              @if (form.controls.unitPriceInput.touched && form.controls.unitPriceInput.errors?.['required']) {
                <p hlmFieldError>
                  {{ translate('billingItems.field.unitPriceRequired', 'Please enter a unit price.') }}
                </p>
              } @else if (form.controls.unitPriceInput.touched && form.controls.unitPriceInput.errors?.['localeDecimal']) {
                <p hlmFieldError>
                  {{ translate('billingItems.field.unitPriceInvalid', 'Enter a valid number.') }}
                </p>
              } @else if (form.controls.unitPriceInput.touched && form.controls.unitPriceInput.errors?.['creditPriceZero']) {
                <p hlmFieldError>
                  {{ translate('billingItems.field.creditPriceZero', 'A credit needs a unit price other than 0.') }}
                </p>
              }
            </hlm-form-field>
          </div>

          <hlm-form-field>
            <label hlmLabel for="addBillingChargeOn">
              {{ translate('billingItems.field.chargeOn', 'Billing Date') }}
            </label>
            <input
              id="addBillingChargeOn"
              hlmInput
              type="date"
              formControlName="chargeOn"
            />
          </hlm-form-field>
        </div>
      </div>
    </form>
  </section>

  <footer hlmDialogFooter>
    <div class="mr-auto flex min-w-0 items-baseline gap-2">
      <span
        class="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground"
      >
        {{ translate('billingItems.dialog.total', 'Total') }}
      </span>
      <span class="text-base font-semibold tabular-nums text-foreground">
        {{ livePreviewTotal() || '—' }}
      </span>
    </div>
    <button type="button" hlmBtn variant="outline" (click)="cancel()">
      {{ translate('billingItems.dialog.cancel', 'Cancel') }}
    </button>
    <button
      type="button"
      hlmBtn
      variant="default"
      (click)="submit()"
      [disabled]="addDisabled()"
    >
      @if (submitting()) {
        <hlm-spinner size="16" />
      } @else {
        <hlm-icon name="lucidePlus" size="0.9rem" />
      }
      {{ translate('billingItems.dialog.add', 'Add') }}
    </button>
  </footer>
</hlm-dialog-surface>
